// #1798 — the two client-side decisions in the Google Calendar connect flow,
// lifted out of useGoogleCalendar.tsx and GoogleCallback.tsx so they can be
// mutation-tested without rendering either.
//
// Background, briefly: from the Median iOS app the consent screen opens in
// external Safari, Google 302s back to /google/callback in Safari, and iOS does
// not fire a Universal Link for a redirect arrival (#626). The callback used to
// render a "Tap to return to Kin" button whose handler was a same-URL reload —
// which cannot fire one either. So the flow dead-ended, for every iPhone user,
// since the June 1 2026 cutover. The ticket (OAuth `state`) is how the Safari
// page now finishes the job without a Kin session.

export interface GoogleAuthUrlArgs {
  clientId: string;
  redirectUri: string;
  scopes: string;
  /** The one-time connect ticket minted by google-calendar-auth. Travels as
   *  OAuth `state`, so Google echoes it back on the callback URL. Null when the
   *  mint failed — the URL still works for the in-session paths. */
  ticket?: string | null;
}

export function buildGoogleAuthUrl({ clientId, redirectUri, scopes, ticket }: GoogleAuthUrlArgs): string {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: scopes,
    access_type: "offline",
    prompt: "consent",
  });
  if (ticket) params.set("state", ticket);
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

export type GoogleCallbackPath =
  /** Nothing to do — no code and no error on the URL. */
  | "no_code"
  /** Rendering where a Kin session exists (inside the Median shell, or a
   *  desktop/Android browser tab opened from a signed-in tab): the existing
   *  session-authenticated `exchange`. */
  | "exchange_with_session"
  /** Rendering in iPhone Safari, which has no Kin session, WITH a ticket on the
   *  URL: hand code + ticket to the server and let the ticket name the user. */
  | "exchange_with_ticket"
  /** iPhone Safari with NO ticket — a stale link minted before #1798. The only
   *  honest thing left is to say so. */
  | "safari_fallback";

export interface CallbackSignals {
  hasCode: boolean;
  hasState: boolean;
  isMedian: boolean;
  isMobileApple: boolean;
  /** Does THIS browser hold a Kin session? True for the iPhone Safari web app
   *  (same-tab navigation back to a signed-in tab); false for external Safari
   *  opened from the Median shell. Decides what happens AFTER a ticket exchange
   *  — stay and route home, or tell her to go back to the app. */
  hasSession: boolean;
}

/**
 * Which way the callback page should try to finish.
 *
 * ⚠️ Order matters, twice over.
 *
 * The Median check comes before the iPhone check because the Median shell's UA
 * also says iPhone — a Median render is NOT the Safari case, it has the session
 * and must use it. Same ordering the page has had since May; this just names it.
 *
 * On iPhone Safari the TICKET wins over a session when both are present. The
 * ticket was minted by the account that tapped Connect; a Safari session may
 * belong to a different, older login on the same phone. Binding the Google
 * tokens to the ticket's user is the correct answer; the session then only
 * decides where to send her afterwards (see `afterTicketSuccess`).
 */
export function chooseGoogleCallbackPath(s: CallbackSignals): GoogleCallbackPath {
  if (!s.hasCode) return "no_code";
  if (s.isMedian) return "exchange_with_session";
  if (s.isMobileApple) {
    if (s.hasState) return "exchange_with_ticket";
    // No ticket: a link minted before #1798. If this tab is signed in (the
    // iPhone Safari web app), the session path still works; otherwise there is
    // nothing left but to say so.
    return s.hasSession ? "exchange_with_session" : "safari_fallback";
  }
  return "exchange_with_session";
}

export type AfterTicketSuccess =
  /** Signed in here — this IS the app (iPhone Safari web). Route home like the
   *  session path does. */
  | "route_home"
  /** Not signed in here — external Safari opened from the Median shell. The
   *  app is a different process; tell her to go back to it. */
  | "tell_her_to_return_to_app";

/**
 * After a ticket exchange succeeds: is this tab the app, or a detour from it?
 *
 * #1798 (Copilot on PR #1804): the iPhone Safari WEB app takes the ticket path
 * too — its callback guard never attempted the session exchange — and was then
 * shown "head back to the Kin app" with no way home, despite being the app. The
 * session is what tells the two apart.
 */
export function afterTicketSuccess(hasSession: boolean): AfterTicketSuccess {
  return hasSession ? "route_home" : "tell_her_to_return_to_app";
}
